// File: src/components/ReadingRoom/ResourceCard.jsx
// Description: Enhanced resource card component with improved UI and interactions

import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { 
  EyeIcon,
  ArrowDownTrayIcon,
  BookOpenIcon,
  ClockIcon,
  TagIcon,
  StarIcon,
  ShareIcon
} from '@heroicons/react/24/outline';
import { 
  HeartIcon as HeartSolidIcon,
  BookmarkIcon as BookmarkSolidIcon
} from '@heroicons/react/24/solid';

const ResourceCard = ({ 
  resource, 
  onReadOnline, 
  onDownload,
  categoryLabel,
  isLoading = false,
  showActions = true,
  compact = false
}) => {
  const { currentLanguage, getText, formatDate } = useLanguage();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = (e) => {
    setImageError(true);
    e.target.src = '/images/default-book.jpg';
  };

  const handleBookmark = (e) => {
    e.stopPropagation();
    setIsBookmarked(!isBookmarked);
    // In real app, this would save to user preferences
  };

  const handleFavorite = (e) => {
    e.stopPropagation();
    setIsFavorited(!isFavorited);
    // In real app, this would save to user preferences
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({
          title: getText(resource.title),
          text: getText(resource.description),
          url: window.location.href
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'difficulty-beginner';
      case 'intermediate': return 'difficulty-intermediate';
      case 'advanced': return 'difficulty-advanced';
      default: return 'difficulty-beginner';
    }
  };

  const formatFileSize = (size) => {
    return size; // Already formatted in mock data
  };

  return (
    <article className={`resource-card ${compact ? 'compact' : ''} ${isLoading ? 'loading' : ''}`}>
      {/* Card Header with Image */}
      <div className="resource-header">
        <div className="resource-image-container">
          {!imageLoaded && !imageError && (
            <div className="image-placeholder">
              <BookOpenIcon className="placeholder-icon" />
            </div>
          )}
          <img
            src={resource.coverImage || '/images/default-book.jpg'}
            alt={getText(resource.title)}
            className={`resource-image ${imageLoaded ? 'loaded' : ''}`}
            onLoad={handleImageLoad}
            onError={handleImageError}
            loading="lazy"
          />
          
          {/* Category Badge */}
          <div className="category-badge">
            <span className="category-text">{categoryLabel}</span>
          </div>

          {/* Difficulty Badge */}
          {resource.difficulty && (
            <div className={`difficulty-badge ${getDifficultyColor(resource.difficulty)}`}>
              {getText({
                en: resource.difficulty.charAt(0).toUpperCase() + resource.difficulty.slice(1),
                mm: resource.difficulty === 'beginner' ? 'အစပြု' : 
                    resource.difficulty === 'intermediate' ? 'အလယ်အလတ်' : 'အဆင့်မြင့်'
              })}
            </div>
          )}

          {/* Quick Actions Overlay */}
          <div className="quick-actions-overlay">
            <button
              onClick={handleBookmark}
              className={`quick-action-btn bookmark-btn ${isBookmarked ? 'active' : ''}`}
              title={getText({
                en: isBookmarked ? 'Remove Bookmark' : 'Add Bookmark',
                mm: isBookmarked ? 'မှတ်သားမှု ဖယ်ရှားရန်' : 'မှတ်သားရန်'
              })}
            >
              <BookmarkSolidIcon className="action-icon" />
            </button>
            
            <button
              onClick={handleFavorite}
              className={`quick-action-btn favorite-btn ${isFavorited ? 'active' : ''}`}
              title={getText({
                en: isFavorited ? 'Remove from Favorites' : 'Add to Favorites',
                mm: isFavorited ? 'အကြိုက်ဆုံးများမှ ဖယ်ရှားရန်' : 'အကြိုက်ဆုံးများတွင် ထည့်ရန်'
              })}
            >
              <HeartSolidIcon className="action-icon" />
            </button>

            <button
              onClick={handleShare}
              className="quick-action-btn share-btn"
              title={getText({
                en: 'Share Resource',
                mm: 'အရင်းအမြစ် မျှဝေရန်'
              })}
            >
              <ShareIcon className="action-icon" />
            </button>
          </div>
        </div>
      </div>

      {/* Card Content */}
      <div className="resource-content">
        {/* Title */}
        <h3 className="resource-title" title={getText(resource.title)}>
          {getText(resource.title)}
        </h3>
        
        {/* Author */}
        <p className="resource-author">
          <span className="author-label">
            {getText({
              en: 'By',
              mm: 'စာရေးသူ'
            })}
          </span>
          <span className="author-name">{getText(resource.author)}</span>
        </p>

        {/* Description */}
        {resource.description && !compact && (
          <p className="resource-description">
            {getText(resource.description)}
          </p>
        )}

        {/* Tags */}
        {resource.tags && resource.tags.length > 0 && !compact && (
          <div className="resource-tags">
            <TagIcon className="tags-icon" />
            <div className="tags-list">
              {resource.tags.slice(0, 3).map((tag, index) => (
                <span key={index} className="tag">
                  {tag}
                </span>
              ))}
              {resource.tags.length > 3 && (
                <span className="tag more-tags">
                  +{resource.tags.length - 3}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Metadata */}
        <div className="resource-metadata">
          <div className="metadata-row">
            <div className="file-info">
              <span className="file-format">{resource.format}</span>
              <span className="file-separator">•</span>
              <span className="file-size">{formatFileSize(resource.fileSize)}</span>
            </div>
            
            <div className="publish-info">
              <ClockIcon className="clock-icon" />
              <span className="publish-date">
                {formatDate(resource.publishDate, { 
                  year: 'numeric', 
                  month: 'short', 
                  day: 'numeric' 
                })}
              </span>
            </div>
          </div>

          {/* Rating/Stats (if available) */}
          {resource.rating && (
            <div className="resource-rating">
              <div className="stars">
                {[...Array(5)].map((_, i) => (
                  <StarIcon 
                    key={i} 
                    className={`star ${i < Math.floor(resource.rating) ? 'filled' : ''}`} 
                  />
                ))}
              </div>
              <span className="rating-text">
                {resource.rating} ({resource.reviewCount || 0})
              </span>
            </div>
          )}
        </div>

        {/* Actions */}
        {showActions && (
          <div className="resource-actions">
            <button
              onClick={() => onReadOnline(resource)}
              className="action-btn primary-action read-btn"
              disabled={isLoading}
            >
              <EyeIcon className="btn-icon" />
              <span className="btn-text">
                {getText({
                  en: 'Read Online',
                  mm: 'အွန်လိုင်းဖတ်ရှုရန်'
                })}
              </span>
            </button>
            
            <button
              onClick={() => onDownload(resource)}
              className="action-btn secondary-action download-btn"
              disabled={isLoading}
            >
              <ArrowDownTrayIcon className="btn-icon" />
              <span className="btn-text">
                {getText({
                  en: 'Download',
                  mm: 'ဒေါင်းလုဒ်'
                })}
              </span>
              {isLoading && <div className="loading-spinner"></div>}
            </button>
          </div>
        )}
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner large"></div>
            <span className="loading-text">
              {getText({
                en: 'Processing...',
                mm: 'လုပ်ဆောင်နေသည်...'
              })}
            </span>
          </div>
        </div>
      )}
    </article>
  );
};

export default ResourceCard;